import { defineStore } from "pinia";
import { computed, reactive, ComputedRef } from "vue";
import {
  AssetManagementPlanMaster,
  DatabaseObject,
  Role,
  PlannerGroup,
  User,
  CostCenter,
  AssetClass,
} from "@/classes";
import { useAuthStore } from "./auth";

export interface ListState {
  users: User[];
  roles: Role[];
  plannerGroups: PlannerGroup[];
  costCenters: CostCenter[];
  assetClasses: AssetClass[];
  assetManagementPlanMasters: AssetManagementPlanMaster[];
}

export type ListKey = keyof ListState;
export type ListType = ListState[ListKey];

const ConfigClasses: Record<ListKey, typeof DatabaseObject> = {
  users: User,
  roles: Role,
  plannerGroups: PlannerGroup,
  costCenters: CostCenter,
  assetClasses: AssetClass,
  assetManagementPlanMasters: AssetManagementPlanMaster,
};

export interface ListStoreState {
  lists: ListState;
  fetched: Record<ListKey, boolean>;
}

export const useListStore = defineStore("list", () => {
  const state: ListStoreState = reactive({
    lists: {
      users: [],
      roles: [],
      plannerGroups: [],
      costCenters: [],
      assetClasses: [],
      assetManagementPlanMasters: [],
    },
    fetched: {
      users: false,
      roles: false,
      plannerGroups: false,
      costCenters: false,
      assetClasses: false,
      assetManagementPlanMasters: false,
    },
  });

  const useList = <T>(key: ListKey): ComputedRef<T[]> => {
    const auth = useAuthStore();

    const list: ComputedRef<T[]> = computed(() => {
      return state.lists[key] as unknown as T[];
    });
    if (!state.fetched[key] && auth.state.user?.id) {
      state.fetched[key] = true;
      ConfigClasses[key].list().then((data) => {
        // @ts-ignore
        state.lists[key] = data;
      });
    }
    return list;
  };

  const useFilteredList = <T>(key: ListKey, filter: (item: T) => boolean) => {
    useList<T>(key);
    return computed(() => {
      return (state.lists[key] as unknown as T[]).filter(filter);
    });
  };

  const refreshList = (key: ListKey) => {
    state.fetched[key] = false;
    useList(key);
  };

  const refreshLists = () => {
    Object.keys(state.fetched).forEach((key) => {
      refreshList(key as ListKey);
    });
  };

  return {
    useList,
    refreshList,
    refreshLists,
    useFilteredList,
  };
});
